import { Paper } from '@mantine/core';
import { SeatingChartProvider } from '../hoc/SeatingChart/SeatingChart.hooks';
import { RuleStateProvider } from '../data/Rule.state';
import { BulletinModal } from '../hoc/Bulletin/BulletinModal';
import { InviteModal } from '../hoc/InviteModal/InviteModal';
import { VersionNotificationBanner } from '../hoc/Banners/VersionNotificationBanner';
import { SupportActivationBanner } from '../hoc/Banners/SupportActivationBanner';
import { BulkStateProvider } from '../data/Bulk.state';
import { PlanExpirationBanner } from '../hoc/Banners/PlanExpirationBanner';
import { UnpaidInvoiceBanner } from '../hoc/Banners/UnpaidInvoiceBanner';
import { IntegrationProblemBanner } from '../hoc/Banners/IntegrationProblemBanner';
import { ListingStateProvider } from '../data/Listing.state';
import { useQueryParam } from '../utils/use-query-param';
import { useEffect, useMemo } from 'react';
import { getApiInventory, getApiPricingRules, useGetApiInventory, useGetApiInventoryListing } from '../api';
import { keepPreviousData } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  forceRuleIdAtom,
  initialPageLoadAtom,
  searchResultsAtom,
  selectedEventAtom,
  selectedSearchEventsAtom,
  selectedTenantListingIdAtom,
  transientGlobalStateAtom,
} from '../data/atoms';
import { TenantIdListingId } from '../models/tenantIdListingId';
import { NowPricing } from '../hoc/NowPricing/NowPricing';
import { SeatingChart } from '../hoc/SeatingChart/SeatingChart';
import { MarketListings } from '../hoc/MarketListings/MarketListings';
import { useTokenRefresh } from '../components/hooks/useTokenRefresh';
import { useFlag } from '@unleash/proxy-client-react';

export default function RuleIsolation() {
  return (
    <ListingStateProvider>
      <RuleStateProvider>
        <BulkStateProvider>
          <BulletinModal />
          <InviteModal />
          <div className="outer-appWrap rule-isolated">
            <VersionNotificationBanner />
            <SupportActivationBanner />
            <PlanExpirationBanner />
            <UnpaidInvoiceBanner />
            <IntegrationProblemBanner />
            <RuleIsolationInterior />
          </div>
        </BulkStateProvider>
      </RuleStateProvider>
    </ListingStateProvider>
  );
}

function RuleIsolationInterior() {
  // TODO: Move this state into a yasml hook.
  const { handleAccessTokenSilently } = useTokenRefresh();
  const ruleIsolationFlag = useFlag('rule-isolation');
  const eventId = useQueryParam('eventId', '');
  useEffect(() => {
    handleAccessTokenSilently();
  }, [handleAccessTokenSilently]);

  const setInitialPageLoad = useSetAtom(initialPageLoadAtom);
  useEffect(() => {
    setInitialPageLoad(false);
  }, [setInitialPageLoad]);

  // Fetches the deep-linked event/listing and pushes the result into global state. All of the
  // "set" work now lives inside the query, so this page no longer needs a data-loading effect.
  useIsolatedRuleBootstrap();

  if (!eventId) {
    return null;
  }

  if (!ruleIsolationFlag) {
    return null;
  }

  return (
    <div className="inner-appWrap">
      <div className="isolated-rule-appWrap">
        <div className="isolated-rule-col-1">
          <Paper className="seatingChartWrapper" pos="relative">
            <SeatingChartProvider>
              <SeatingChart />
            </SeatingChartProvider>
          </Paper>
        </div>
        <div className="isolated-rule-col-2">
          <NowPricing />
          <MarketListings />
        </div>
      </div>
    </div>
  );
}

// Replaces the old `getApiInventory(...).then(...)` effect. The query is gated with `enabled`
// (the same guard the effect used) and runs once (`staleTime: Infinity`); the state writes that
// used to live in the effect's `.then` now run inside `queryFn`.
function useIsolatedRuleBootstrap() {
  const eventId = useQueryParam('eventId', '');
  const itemId = useQueryParam('itemId', '');
  const referenceId = useQueryParam('referenceId', '');

  const setForceRuleId = useSetAtom(forceRuleIdAtom);
  const setSelectedEvent = useSetAtom(selectedEventAtom);
  const setSearchResults = useSetAtom(searchResultsAtom);
  const setSelectedListing = useSetAtom(selectedTenantListingIdAtom);
  const setSelectedSearchEvents = useSetAtom(selectedSearchEventsAtom);
  const setTransientGlobalState = useSetAtom(transientGlobalStateAtom);
  const selectedListing = useAtomValue(selectedTenantListingIdAtom);

  const { data: listingData } = useGetApiInventoryListing(
    { itemId },
    {
      query: {
        placeholderData: keepPreviousData,
        select: (data) => data.data,
        enabled: !!itemId,
        // --- TEST SHIM: pretend the listing lookup takes 2s and resolves listingId to itemId. ---
        // TODO: remove and restore the generated queryFn before merging.
        // queryKey: ['test-delayed-listing', itemId],
        // staleTime: Infinity,
        // queryFn: async () => {
        //   await new Promise((resolve) => setTimeout(resolve, 2000));
        //   return { data: { listingId: itemId } } as unknown as Awaited<ReturnType<typeof getApiInventoryListing>>;
        // },
      },
    },
  );
  const listingId = listingData?.listingId;

  const defaultSearchDates = useMemo(
    () => ({
      fromDate: new Date(),
      toDate: dayjs().add(100, 'years').toDate(),
    }),
    [],
  );

  const params = useMemo(
    () => ({
      ...defaultSearchDates,
      eventIds: [eventId],
      ...(listingId ? { listingIds: [listingId] } : {}),
    }),
    [defaultSearchDates, eventId, listingId],
  );

  return useGetApiInventory(params, {
    query: {
      // Mirrors the old guard: need an event, no listing chosen yet, and (when an itemId is
      // present) wait for its resolved listingId.
      enabled: !!eventId && !selectedListing.hasValue() && !(itemId && !listingId),
      // Bootstrap fetch: run once, and use a dedicated key so this side-effecting queryFn never
      // collides with other inventory consumers sharing the generated key.
      staleTime: Infinity,
      queryKey: ['rule-isolation-inventory', { eventId, listingId, referenceId }],
      queryFn: async ({ signal }) => {
        const result = await getApiInventory(params, { signal });
        if (result.data) {
          setSearchResults(result.data);
          setSelectedSearchEvents(result.data.events);
          const event = result.data.events[0];
          if (event) {
            setSelectedEvent(event);
            const listing = result.data.listings.find((_listing) => _listing.eventId === event.eventId);
            if (listing && itemId) {
              setSelectedListing(TenantIdListingId.from(listing.tenantId, listing.listingId).toStringTyped());
            }
            if (!listing?.ruleId && referenceId) {
              const ruleResponse = await getApiPricingRules({ referenceId });
              if (ruleResponse.data?.ruleId) {
                setForceRuleId(ruleResponse.data.ruleId);
                setTransientGlobalState((s) => ({ ...s, autoUpdateAutoPricedListPriceRuleId: ruleResponse.data.ruleId }));
              } else {
                setForceRuleId(referenceId);
              }
            }
          }
        } else if (referenceId) {
          setForceRuleId(referenceId);
        }
        return result;
      },
    },
  });
}
